import { Button, useMediaQuery, useTheme } from '@mui/material';
import * as Sentry from '@sentry/react';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { SCHOOL_GROUPS_PATH, SCHOOL_LANDING_HIDE_PATH, SCHOOLS_PATH } from '../../functions/src/admin/paths';
import { dbKeyMap, domainMap } from '../../functions/src/api/dbConfig';
import { buildSchoolSelectorSchools, schoolSelectorEligibleKeys } from '../../functions/src/api/schoolSelectorLogic';
import type { SchoolWithKey } from '../../functions/src/types';
import { getDatabase } from '../api/firebase';
import { useDatabase } from '../api/firebaseHooks';
import { langContext } from '../api/Lang';
import { getSchoolDisplayName } from '../api/schoolDisplayName';
import { useStrings } from '../common-strings';
import { useSchoolLandingStrings } from './schoolLanding-strings';

type SchoolLandingSchoolData = {
    schoolName: { el: string; en: string } | null;
    schoolLandingCamp: boolean;
};

function SchoolLandingSchoolSubscriber({ schoolKey, onUpdate }: {
    schoolKey: string;
    onUpdate: (key: string, data: SchoolLandingSchoolData | null) => void;
}) {
    // Rules allow public read only on leaf paths (schoolName, schoolLandingCamp), not the whole school node.
    const nameSnap = useDatabase(`${SCHOOLS_PATH}/${schoolKey}/schoolName`);
    const campSnap = useDatabase(`${SCHOOLS_PATH}/${schoolKey}/schoolLandingCamp`);
    useEffect(() => {
        if (nameSnap === null || nameSnap === undefined) return;
        if (!nameSnap.exists()) {
            onUpdate(schoolKey, null);
            return;
        }
        onUpdate(schoolKey, {
            schoolName: nameSnap.val(),
            schoolLandingCamp: campSnap?.exists() === true && campSnap.val() === true,
        });
    }, [nameSnap, campSnap, schoolKey, onUpdate]);
    return null;
}

// Theme configuration for different school groups
type SchoolTheme = {
    leftSvg: {
        width: string;
        height: string;
    };
    rightSvg: {
        width: string;
        height: string;
        minWidth?: string;
        minHeight?: string;
    };
    selectedButton: {
        fillColor: string;
        borderColor: string;
        textColor: string;
    };
};

// Constants for colors that are the same across all schools
const PRIMARY_COLOR = '#1F2937'; // Dark gray from Figma
const BACKGROUND_COLOR = '#FFFFFF';

const schoolThemes: Record<string, SchoolTheme> = {
    'ac': {
        leftSvg: {
            width: '20vw',
            height: '86vh'
        },
        rightSvg: {
            width: '40vw',
            height: '55vh',
            minWidth: '230px',
            minHeight: '120px'
        },
        selectedButton: {
            fillColor: '#FFF3D4',
            borderColor: '#F6C545',
            textColor: '#8A6303'
        }
    },
    'stc': {
        leftSvg: {
            width: '20vw',
            height: '86vh'
        },
        rightSvg: {
            width: '40vw',
            height: '45vh',
            minWidth: '100px',
            minHeight: '120px'
        },
        selectedButton: {
            fillColor: '#C7E1FF',
            borderColor: '#1C3B60',
            textColor: '#1C3B60'
        }
    },
    'acs': {
        leftSvg: {
            width: '20vw',
            height: '86vh'
        },
        rightSvg: {
            width: '40vw',
            height: '45vh',
            minWidth: '100px',
            minHeight: '120px'
        },
        selectedButton: {
            fillColor: '#FFF3D4',
            borderColor: '#F0C048',
            textColor: '#8A6303'
        }
    },
    'campion': {
        leftSvg: {
            width: '20vw',
            height: '86vh'
        },
        rightSvg: {
            width: '40vw',
            height: '45vh',
            minWidth: '100px',
            minHeight: '120px'
        },
        selectedButton: {
            fillColor: '#C1DEFD',
            borderColor: '#224060',
            textColor: '#224060'
        }
    },
    'moraitis': {
        leftSvg: {
            width: '20vw',
            height: '86vh'
        },
        rightSvg: {
            width: '40vw',
            height: '45vh',
            minWidth: '100px',
            minHeight: '120px'
        },
        selectedButton: {
            fillColor: '#BBCDBA',
            borderColor: '#266332',
            textColor: '#266332'
        }
    }
};

export default function SchoolSelector() {
    const [currentGroupKey, setCurrentGroupKey] = useState<string>('');
    const [schoolDataByKey, setSchoolDataByKey] = useState<Record<string, SchoolLandingSchoolData>>({});
    const [defaultLangFetched, setDefaultLangFetched] = useState(false);
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

    // Determine which group to show based on the current domain
    useEffect(() => {
        // Production logic - extract from hostname
        const hostname = window.location.hostname;
        // Extract the group key from the subdomain (e.g., 'ac' from 'ac.lunch-box.gr')
        const groupKey = hostname.split('.')[0];
        setCurrentGroupKey(groupKey);
    }, []);

    const schoolGroupSnap = useDatabase(currentGroupKey ? `${SCHOOL_GROUPS_PATH}/${currentGroupKey}` : '');
    const hideSnap = useDatabase(currentGroupKey ? `${SCHOOL_LANDING_HIDE_PATH}/${currentGroupKey}` : '');

    const schoolGroup = schoolGroupSnap?.exists() ? schoolGroupSnap.val() as Record<string, unknown> : null;
    const schoolLandingHide = hideSnap?.exists() ? hideSnap.val() as Record<string, boolean> : undefined;

    const eligibleKeys = useMemo(
        () => schoolSelectorEligibleKeys(schoolGroup, schoolLandingHide),
        [schoolGroup, schoolLandingHide]
    );

    const eligibleKeysKey = eligibleKeys.join('|');

    useEffect(() => {
        setSchoolDataByKey(prev => {
            const eligibleSet = new Set(eligibleKeys);
            const next = Object.fromEntries(
                Object.entries(prev).filter(([key]) => eligibleSet.has(key))
            ) as Record<string, SchoolLandingSchoolData>;
            return Object.keys(next).length === Object.keys(prev).length ? prev : next;
        });
    }, [eligibleKeysKey]);

    const handleSchoolDataUpdate = useCallback((key: string, data: SchoolLandingSchoolData | null) => {
        setSchoolDataByKey(prev => {
            if (!data) {
                if (!(key in prev)) return prev;
                const next = { ...prev };
                delete next[key];
                return next;
            }
            const prevData = prev[key];
            if (
                prevData?.schoolName?.el === data.schoolName?.el
                && prevData?.schoolName?.en === data.schoolName?.en
                && prevData?.schoolLandingCamp === data.schoolLandingCamp
            ) {
                return prev;
            }
            return { ...prev, [key]: data };
        });
    }, []);

    const allSchoolsForSelector = useMemo(() => Object.fromEntries(
        Object.entries(schoolDataByKey).map(([key, data]) => [key, {
            schoolName: data.schoolName ?? undefined,
            schoolLandingCamp: data.schoolLandingCamp,
        }])
    ), [schoolDataByKey]);

    const selectorResources = useMemo(() => ({
        schoolGroup,
        schoolLandingHide,
        allSchools: allSchoolsForSelector,
    }), [schoolGroup, schoolLandingHide, allSchoolsForSelector]);

    const regularSchools = useMemo(
        () => buildSchoolSelectorSchools(selectorResources, { landingSection: 'school' }),
        [selectorResources]
    );

    const campSchools = useMemo(
        () => buildSchoolSelectorSchools(selectorResources, { landingSection: 'camp' }),
        [selectorResources]
    );

    const loading = !!currentGroupKey && (schoolGroupSnap === null || schoolGroupSnap === undefined);
    const error = schoolGroupSnap != null && !schoolGroupSnap.exists() ? 'Failed to load schools' : null;

    // Fetch default language from the first school in the group
    useEffect(() => {
        const schoolsForLang = regularSchools.length > 0 ? regularSchools : campSchools;
        if (schoolsForLang.length > 0 && !defaultLangFetched) {
            const firstSchool = schoolsForLang[0];
            const schoolKey = firstSchool.key;

            // Get the database URL for this school
            const domain = dbKeyMap[schoolKey];

            if (domain && domainMap[domain]) {
                const databaseURL = domainMap[domain].databaseURL;
                const schoolDb = getDatabase(databaseURL);

                // Read default language from school's local database
                schoolDb.ref('langs/defaultLang').once('value')
                    .then(snapshot => {
                        const lang = snapshot.val() || 'en';
                        // Set the language using the HTML function
                        (window as any).setLang(lang);
                        setDefaultLangFetched(true);
                    })
                    .catch(err => {
                        Sentry.captureException(err, {
                            tags: {
                                component: 'SchoolSelector',
                                action: 'fetch_default_language'
                            },
                            extra: {
                                schoolKey,
                                domain,
                                databaseURL
                            }
                        });
                        // Fallback to 'en'
                        (window as any).setLang('en');
                        setDefaultLangFetched(true);
                    });
            } else {
                // Fallback if we can't get the database
                (window as any).setLang('en');
                setDefaultLangFetched(true);
            }
        }
    }, [regularSchools, campSchools, defaultLangFetched]);

    // Get theme for current school group
    const theme = schoolThemes[currentGroupKey];

    // Get current language from context
    const [currentLang] = useContext(langContext);
    const commonStrings = useStrings();
    const landingStrings = useSchoolLandingStrings();

    const schoolDataSubscribers = eligibleKeys.map(key => (
        <SchoolLandingSchoolSubscriber key={key} schoolKey={key} onUpdate={handleSchoolDataUpdate} />
    ));


    // If no theme is found, show an error
    if (!theme) {
        return (
            <>
                {schoolDataSubscribers}
                <div style={{ 
                    textAlign: 'center', 
                    padding: '20px',
                    color: '#e74c3c',
                    fontFamily: 'Roboto'
                }}>
                    <p>{landingStrings.noTheme}</p>
                </div>
            </>
        );
    }

    if (loading) {
        return (
            <>
                {schoolDataSubscribers}
                <div style={{ 
                    textAlign: 'center', 
                    padding: '20px',
                    color: PRIMARY_COLOR,
                    fontFamily: 'Roboto'
                }}>
                    <p>{commonStrings.loading}</p>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                {schoolDataSubscribers}
                <div style={{ 
                    textAlign: 'center', 
                    padding: '20px',
                    color: '#e74c3c',
                    fontFamily: 'Roboto'
                }}>
                    <p>{landingStrings.errorLoadingSchools}</p>
                </div>
            </>
        );
    }

    if (regularSchools.length === 0 && campSchools.length === 0) {
        return (
            <>
                {schoolDataSubscribers}
                <div style={{ 
                    textAlign: 'center', 
                    padding: '20px',
                    color: '#e74c3c',
                    fontFamily: 'Roboto'
                }}>
                    <p>{landingStrings.noSchoolsForGroup}</p>
                </div>
            </>
        );
    }

    const totalSchoolCount = regularSchools.length + campSchools.length;

    const handleSchoolClick = (schoolDomain: string) => {
        // Log school selection (performance monitoring removed for Developer plan)
        Sentry.withScope((scope) => {
            scope.setContext("school_selection", {
                selected_school_domain: schoolDomain,
                group_key: currentGroupKey
            });
            Sentry.captureMessage("School selected", "info");
        });

        // For single-school groups (like campion), navigate to /order on current domain
        // For multi-school groups (like ac, stc), navigate to the specific school domain
        if (totalSchoolCount === 1) {
            // Single school - navigate to /order on current domain
            window.location.replace(window.location.origin + '/order');
        } else {
            // Multiple schools - navigate to the specific school domain
            window.location.href = `https://${schoolDomain}/order`;
        }
    };

    const renderSchoolButtons = (schoolList: SchoolWithKey[]) => (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: isSmallMobile ? '12px' : '16px',
            alignItems: 'center'
        }}>
            {schoolList.map((school) => (
                <Button
                    key={school.key}
                    onClick={(e) => {
                        e.preventDefault(); // Prevent any default behavior
                        handleSchoolClick(school.schoolDomain);
                    }}
                    variant="outlined"
                    sx={{
                        width: isSmallMobile ? '280px' : isMobile ? '340px' : '400px',
                        height: isSmallMobile ? '56px' : '69px',
                        background: '#F5F7FA',
                        border: '2px solid #E7E7EE',
                        borderRadius: '16px',
                        fontFamily: 'Roboto',
                        fontSize: isSmallMobile ? '16px' : '18px',
                        fontWeight: '500',
                        color: PRIMARY_COLOR,
                        textTransform: 'none',
                        '&:hover': {
                            background: theme.selectedButton.fillColor,
                            border: `2px solid ${theme.selectedButton.borderColor}`,
                            color: theme.selectedButton.textColor,
                            transform: 'translateY(-2px)',
                            boxShadow: `0 10px 20px ${theme.selectedButton.borderColor}30`
                        }
                    }}
                >
                    {getSchoolDisplayName(school, school.key, currentLang)}
                </Button>
            ))}
        </div>
    );

    const renderSelectionSection = (heading: string, description: string, schoolList: SchoolWithKey[]) => (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: isSmallMobile ? '24px' : isMobile ? '28px' : '32px'
        }}>
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
            }}>
                <h1 style={{
                    color: PRIMARY_COLOR,
                    fontFamily: 'Roboto',
                    fontSize: isSmallMobile ? '24px' : isMobile ? '28px' : '32px',
                    fontWeight: 'bold',
                    margin: '0',
                    textAlign: 'center'
                }}>
                    {heading}
                </h1>
                <p style={{
                    color: 'rgba(0, 0, 0, 0.6)',
                    fontFamily: 'Roboto',
                    fontSize: isSmallMobile ? '14px' : isMobile ? '15px' : '16px',
                    margin: '0',
                    textAlign: 'center',
                    lineHeight: '1.5'
                }}>
                    {description}
                </p>
            </div>
            {renderSchoolButtons(schoolList)}
        </div>
    );

    // Single layout for all schools - only theme colors and logo change
    return (
        <>
            {schoolDataSubscribers}
            <div style={{
                minHeight: '100vh',
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: BACKGROUND_COLOR,
                position: 'relative',
                overflowX: 'hidden',
                paddingTop: isSmallMobile ? '88px' : isMobile ? '100px' : '120px',
                paddingBottom: isSmallMobile ? '24px' : '40px'
            }}>
                {/* Background SVG shapes */}
                <div style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    width: '50vw',
                    height: '100vh',
                    zIndex: 0,
                    pointerEvents: 'none'
                }}>
                    <img
                        src={`../img-assets/${currentGroupKey}-landing-l.svg`}
                        alt=""
                        style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            width: theme.leftSvg.width,
                            height: theme.leftSvg.height,
                            objectPosition: 'bottom left',
                            objectFit: 'contain'
                        }}
                    />
                </div>
                <div style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    width: '50vw',
                    height: '100vh',
                    zIndex: 0,
                    pointerEvents: 'none'
                }}>
                    <img
                        src={`../img-assets/${currentGroupKey}-landing-r.svg`}
                        alt=""
                        style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: theme.rightSvg.width,
                            height: theme.rightSvg.height,
                            minWidth: theme.rightSvg.minWidth,
                            minHeight: theme.rightSvg.minHeight,
                            objectPosition: 'top right',
                            objectFit: 'contain'
                        }}
                    />
                </div>

                {/* Main container */}
                <div style={{
                    textAlign: 'center',
                    maxWidth: '600px',
                    width: '90%',
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: isSmallMobile ? '40px' : isMobile ? '50px' : '64px'
                }}>
                    {/* School Logo */}
                    <div style={{ marginBottom: '0' }}>
                        <img
                            src={`../img-assets/school-logos/logo-${currentGroupKey}.png`}
                            alt={commonStrings.schoolLogoAlt}
                            style={{
                                maxWidth: isSmallMobile ? '250px' : isMobile ? '300px' : '400px',
                                maxHeight: isSmallMobile ? '100px' : isMobile ? '120px' : '140px',
                                objectFit: 'contain'
                            }}
                        />
                    </div>

                    {/* Text and Options Section */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: isSmallMobile ? '40px' : isMobile ? '48px' : '56px'
                    }}>
                        {regularSchools.length > 0 && renderSelectionSection(
                            landingStrings.selectSchoolHeading,
                            landingStrings.selectSchoolDescription,
                            regularSchools
                        )}
                        {campSchools.length > 0 && renderSelectionSection(
                            landingStrings.selectCampHeading,
                            landingStrings.selectCampDescription,
                            campSchools
                        )}
                    </div>

                    {/* Footer with Gefsinus logo */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <p style={{
                            color: 'rgba(0, 0, 0, 0.4)',
                            fontSize: isSmallMobile ? '12px' : '14px',
                            margin: '0',
                            fontFamily: 'Roboto'
                        }}>
                            {landingStrings.foodConceptBy}
                        </p>
                        <img
                            src="../img-assets/logo-gefsinus-dark-el.png"
                            alt={landingStrings.gefsinusAlt}
                            style={{
                                width: isSmallMobile ? '120px' : isMobile ? '140px' : '160px',
                                height: 'auto',
                                objectFit: 'contain'
                            }}
                        />
                    </div>
                </div>

                {/* Lunchbox Logo in top left */}
                <div style={{
                    position: 'fixed',
                    top: isSmallMobile ? '20px' : isMobile ? '30px' : '40px',
                    left: isSmallMobile ? '20px' : isMobile ? '32px' : '48px',
                    zIndex: 2
                }}>
                    <img
                        src="../img-assets/logo-light.svg"
                        alt={landingStrings.lunchboxAlt}
                        style={{
                            width: isSmallMobile ? '160px' : isMobile ? '200px' : '230px',
                            height: 'auto',
                            objectFit: 'contain'
                        }}
                    />
                </div>

                {/* Language Toggle in top right */}
                <div style={{
                    position: 'fixed',
                    top: isSmallMobile ? '20px' : isMobile ? '30px' : '40px',
                    right: isSmallMobile ? '20px' : isMobile ? '30px' : '40px',
                    zIndex: 2
                }}>
                    <button
                        onClick={() => {
                            // Use the HTML toggleLanguage function
                            (window as any).toggleLanguage();
                        }}
                        style={{
                            background: 'white',
                            border: '2px solid #E7E7EE',
                            borderRadius: '16px',
                            padding: isSmallMobile ? '12px 16px' : isMobile ? '14px 20px' : '16px 24px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'pointer',
                            fontFamily: 'Roboto',
                            fontSize: isSmallMobile ? '14px' : '16px',
                            color: PRIMARY_COLOR
                        }}
                    >
                        <img
                            src={landingStrings.languageToggleFlagSrc}
                            alt={landingStrings.languageAlt}
                            style={{
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%'
                            }}
                        />
                        {landingStrings.languageToggleLabel}
                        <span style={{ color: '#9AA5B1' }}>▼</span>
                    </button>
                </div>
            </div>
        </>
    );
}
